import { useEffect, useState } from "react";
import {
  CanAccess,
  type TreeMenuItem,
  useLink,
  useMenu,
  useTranslate,
} from "@refinedev/core";
import {
  ThemedSider,
  useThemedLayoutContext,
  type RefineThemedLayoutSiderProps,
} from "@refinedev/mui";
import Collapse from "@mui/material/Collapse";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Dashboard from "@mui/icons-material/Dashboard";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ListOutlined from "@mui/icons-material/ListOutlined";

/**
 * Refine's sider with our own menu tree. The stock tree opens every section or
 * none (`siderItemsAreCollapsed`); this one opens each section unless its
 * resource sets `meta.defaultCollapsed`, and always opens the section holding
 * the current page. Pages inside a section sit tighter than the stock ones.
 * The drawer, title, collapse button, mobile behaviour and logout stay Refine's.
 */
export const Sider = (props: RefineThemedLayoutSiderProps) => (
  <ThemedSider
    {...props}
    render={({ logout, collapsed }) => (
      <>
        <DashboardItem collapsed={collapsed} />
        <MenuTree collapsed={collapsed} />
        {logout}
      </>
    )}
  />
);

const iconSx = (collapsed: boolean) => ({
  justifyContent: "center",
  minWidth: "24px",
  transition: "margin-right 0.3s",
  marginRight: collapsed ? "0px" : "12px",
  color: "currentColor",
});

const textProps = { noWrap: true, fontSize: "14px" };

const DashboardItem = ({ collapsed }: { collapsed: boolean }) => {
  const Link = useLink();
  const translate = useTranslate();
  const { selectedKey } = useMenu();
  const { setMobileSiderOpen } = useThemedLayoutContext();
  const label = translate("dashboard.title", "Dashboard");
  const isSelected = selectedKey === "/";

  return (
    <CanAccess resource="dashboard" action="list">
      <Tooltip title={label} placement="right" disableHoverListener={!collapsed} arrow>
        <ListItemButton
          component={Link as any}
          to="/"
          selected={isSelected}
          onClick={() => setMobileSiderOpen(false)}
          sx={{
            pl: 2,
            py: 1,
            justifyContent: "center",
            color: isSelected ? "primary.main" : "text.primary",
          }}
        >
          <ListItemIcon sx={iconSx(collapsed)}>
            <Dashboard />
          </ListItemIcon>
          <ListItemText primary={label} primaryTypographyProps={textProps} />
        </ListItemButton>
      </Tooltip>
    </CanAccess>
  );
};

const MenuTree = ({ collapsed }: { collapsed: boolean }) => {
  const Link = useLink();
  const { menuItems, selectedKey, defaultOpenKeys } = useMenu();
  const { setSiderCollapsed, setMobileSiderOpen } = useThemedLayoutContext();

  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      menuItems
        .filter((item) => item.children.length > 0)
        .map((item) => [item.key, !item.meta?.defaultCollapsed]),
    ),
  );

  // Navigating into a closed section opens it, as the stock sider does.
  useEffect(() => {
    setOpen((previous) => ({
      ...previous,
      ...Object.fromEntries(defaultOpenKeys.map((key) => [key, true])),
    }));
  }, [defaultOpenKeys]);

  const toggle = (key: string) => setOpen((previous) => ({ ...previous, [key]: !previous[key] }));

  const renderTree = (tree: TreeMenuItem[]) =>
    tree.map((item) => {
      const { key, icon, label, name, route, children, meta } = item;
      const isNested = meta?.parent !== undefined;

      if (children.length > 0) {
        const isOpen = open[key] ?? false;
        return (
          <CanAccess key={key} resource={name} action="list" params={{ resource: item }}>
            <div>
              <Tooltip title={label ?? name} placement="right" disableHoverListener={!collapsed} arrow>
                <ListItemButton
                  onClick={() => {
                    if (collapsed) {
                      setSiderCollapsed(false);
                      if (!isOpen) toggle(key);
                    } else {
                      toggle(key);
                    }
                  }}
                  sx={{ pl: 2, justifyContent: "center" }}
                >
                  <ListItemIcon sx={iconSx(collapsed)}>{icon ?? <ListOutlined />}</ListItemIcon>
                  <ListItemText primary={label} primaryTypographyProps={textProps} />
                  {isOpen ? (
                    <ExpandLess sx={{ color: "text.icon" }} />
                  ) : (
                    <ExpandMore sx={{ color: "text.icon" }} />
                  )}
                </ListItemButton>
              </Tooltip>
              {!collapsed && (
                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {renderTree(children)}
                  </List>
                </Collapse>
              )}
            </div>
          </CanAccess>
        );
      }

      const isSelected = key === selectedKey;
      return (
        <CanAccess key={key} resource={name} action="list" params={{ resource: item }}>
          <Tooltip title={label ?? name} placement="right" disableHoverListener={!collapsed} arrow>
            <ListItemButton
              component={Link as any}
              to={route}
              selected={isSelected}
              onClick={() => setMobileSiderOpen(false)}
              sx={{
                pl: isNested ? 4 : 2,
                py: isNested ? 0.5 : 1,
                justifyContent: "center",
                color: isSelected ? "primary.main" : "text.primary",
              }}
            >
              <ListItemIcon sx={iconSx(collapsed)}>{icon ?? <ListOutlined />}</ListItemIcon>
              <ListItemText primary={label} primaryTypographyProps={textProps} />
            </ListItemButton>
          </Tooltip>
        </CanAccess>
      );
    });

  return <>{renderTree(menuItems)}</>;
};
